<template>
  <div
    class="adviser-enrolment-wrapper"
    :class="{ 'web-style': cardStyle === 'web' }"
  >
    <div class="adviser-enrolment-content">
      <!-- Hidden form to prevent autofill -->
      <div style="display: none">
        <input type="text" name="fakeusernameremembered" />
        <input type="password" name="fakepasswordremembered" />
      </div>
      <div>
        <h1 id="get-registered">Get Registered</h1>
        <p>
          Get started by submitting your details below to be registered on the
          Old Mutual Will platform.
        </p>
        <ValidationObserver ref="observer">
          <v-form
            ref="form"
            v-model="formValid"
            lazy-validation
            @submit.prevent="submitForm"
            autofocus="false"
            class="adviser-enrolment-form"
            autocomplete="off"
          >
            <v-row>
              <v-col cols="12">
                <ValidationProvider
                  id="first_names"
                  v-slot="{ errors }"
                  name="first_names"
                  rules="required"
                >
                  <v-text-field
                    v-model.trim="formData.firstNames"
                    label="First names*"
                    :error-messages="errors"
                    autocomplete="off"
                    :readonly="false"
                    :outlined="cardStyle === 'web'"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  id="last_name"
                  v-slot="{ errors }"
                  name="last_name"
                  rules="required"
                >
                  <v-text-field
                    v-model.trim="formData.lastName"
                    label="Last name*"
                    :error-messages="errors"
                    :outlined="cardStyle === 'web'"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  id="email"
                  v-slot="{ errors }"
                  name="email"
                  rules="required|email"
                >
                  <v-text-field
                    id="email"
                    v-model.trim="formData.email"
                    :error-messages="errors"
                    label="Email"
                    autocomplete="new-email"
                    :readonly="false"
                    :outlined="cardStyle === 'web'"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  id="company"
                  v-slot="{ errors }"
                  name="channel"
                  rules="required"
                >
                  <v-select
                    :label="companyFieldLabel"
                    :items="sortedCompanies"
                    item-text="text"
                    item-value="value"
                    v-model="formData.companyId"
                    :error-messages="errors"
                    :menu-props="{ offsetY: true, zIndex: 200, maxHeight: 400 }"
                    :outlined="cardStyle === 'web'"
                  ></v-select>
                </ValidationProvider>
              </v-col>
              <v-col v-if="hasSelectedCompany" cols="12">
                <ValidationProvider
                  id="adviserCode"
                  v-slot="{ errors }"
                  name="adviserCode"
                  :rules="adviserCodeRules"
                >
                  <v-text-field
                    label="Adviser Code"
                    v-model="formData.adviserCode"
                    :error-messages="errors"
                    @blur="normaliseAdviserCode"
                    :outlined="cardStyle === 'web'"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  id="password"
                  v-slot="{ errors }"
                  name="password"
                  rules="required|password"
                >
                  <v-text-field
                    v-model="formData.password"
                    :error-messages="errors"
                    :append-icon="showPassword ? icons.mdiEye : icons.mdiEyeOff"
                    :type="showPassword ? 'text' : 'password'"
                    label="Password"
                    @click:append="showPassword = !showPassword"
                    autocomplete="new-password"
                    :readonly="false"
                    :outlined="cardStyle === 'web'"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  id="confirm password"
                  v-slot="{ errors }"
                  name="confirm password"
                  rules="required|confirmed:password"
                >
                  <v-text-field
                    v-model="formData.confirmPassword"
                    :error-messages="errors"
                    :append-icon="
                      showConfirmPassword ? icons.mdiEye : icons.mdiEyeOff
                    "
                    :type="showConfirmPassword ? 'text' : 'password'"
                    label="Confirm Password"
                    @click:append="showConfirmPassword = !showConfirmPassword"
                    autocomplete="new-password"
                    :readonly="false"
                    :outlined="cardStyle === 'web'"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  id="accepted_terms"
                  v-slot="{ errors }"
                  name="accepted_terms"
                  :rules="{ is: [true] }"
                >
                  <v-checkbox
                    v-model="formData.acceptedTerms"
                    color="primary"
                    :error-messages="errors"
                  >
                    <template #label>
                      <div class="terms-and-conditions-container">
                        By signing up, I accept
                        <a
                          :href="
                            $router.resolve({ name: 'TermsAndConditions' }).href
                          "
                          target="_blank"
                          @click.stop
                        >
                          {{ whitelabel.name }}'s terms & conditions
                        </a>
                      </div>
                    </template>
                  </v-checkbox>
                </ValidationProvider>
              </v-col>
            </v-row>
            <v-row justify="center">
              <v-col cols="12" class="text-center">
                <GradientButton
                  :title="'Submit'"
                  :small="true"
                  :callout="'right'"
                  :type="'submit'"
                />
              </v-col>
            </v-row>
          </v-form>
        </ValidationObserver>
        <!-- Channel pages drop the channel selector in here so it sits
             directly below the Get Registered form card. -->
        <slot name="channelSelector" />
        <v-row>
          <v-col cols="12" class="mt-12">
            <p>
              If you have any questions about the registration process, please
              don't hesitate to contact us at
              <a href="mailto:adviser@oldmutualwill.co.za"
                >adviser@oldmutualwill.co.za</a
              >
            </p>
          </v-col>
        </v-row>
        <div v-if="showFaqsBanner" class="download-faqs-banner-container">
          <div class="download-faqs-banner">
            <div class="content elevation-5">
              <p>
                <span>{{ faqsBannerTitle }}</span>
              </p>
              <div class="download-faqs-banner-button">
                <GradientButton
                  title="DOWNLOAD PDF"
                  :type="'button'"
                  @gradientButtonClicked="downloadFAQs"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="showWhatsNewBanner"
          class="download-faqs-banner-container customer-brochure"
        >
          <div class="download-faqs-banner">
            <div class="content elevation-5">
              <p>
                <span>From Will to OMP Sale</span>
              </p>
              <div class="download-faqs-banner-button">
                <GradientButton
                  title="DOWNLOAD PDF"
                  :type="'button'"
                  @gradientButtonClicked="downloadWhatsNew"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="showCustomerBrochureBanner"
          class="download-faqs-banner-container customer-brochure"
        >
          <div class="download-faqs-banner">
            <div class="content elevation-5">
              <p>
                <span>{{ customerBrochureBannerTitle }}</span>
              </p>
              <div class="download-faqs-banner-button">
                <GradientButton
                  title="DOWNLOAD PDF"
                  :type="'button'"
                  @gradientButtonClicked="downloadCustomerBrochure"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- <v-img
        class="mb-12"
        :src="
          require(`@/assets/old_mutual/adviser-enrolment/adviser-enrolment-image1.png`)
        "
      /> -->

      <div v-if="showFaqs" class="faq">
        <h1>Frequently Asked Questions</h1>
        <div
          v-for="(category, index) in displayedFaqCategories"
          :key="index"
          class="faq-category"
        >
          <h2 class="om-green">
            {{
              index === 0 && !faqCategoriesOverride
                ? legalitySecurityTitle
                : category.title
            }}
          </h2>
          <div
            v-for="(faq, faqIndex) in category.faqs"
            :key="faqIndex"
            class="faq-item"
          >
            <p class="question mb-2 semi-bold">Q: {{ getFaqQuestion(faq) }}</p>
            <p
              class="answer"
              v-html="getFaqAnswer(faq)"
              @click="onFaqAnswerClick"
            ></p>
          </div>
        </div>
      </div>
      <div v-if="displayedTerms.length" id="terms" class="terms mb-12 pb-12">
        <h1>Terms and Conditions</h1>
        <ol>
          <li v-for="(term, index) in displayedTerms" :key="index">
            {{ getTerm(term, index) }}
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script>
  import ErrorSnackbar from '@/components/common/ErrorSnackbar.vue';
  import { smoothScrollToAnchor } from '@/helpers/dom/smooth-scroll-to-anchor';
  import { ValidationProvider, ValidationObserver } from 'vee-validate';
  import {
    REGISTER_ADVISER_MUTATION,
    ADVISER_ENROLMENT_COMPANIES_QUERY,
  } from '@/constants/graphql/advisors';
  import * as Sentry from '@sentry/browser';

  const MFC_COMPANY_IDS = [27, 42];
  const NON_PRODUCTION_MFC_TEST_COMPANY_IDS = [14];

  export default {
    name: 'AdviserEnrolmentContent',
    components: {
      ValidationProvider,
      ValidationObserver,
      ErrorSnackbar,
    },
    props: {
      // Legacy 2026-01-01 incentive switchover, still passed by /adviser.
      // The channel pages omit it — no date means after-switchover copy.
      switchoverDate: {
        type: String,
        default: null,
      },
      // Restricts the Company dropdown to these IDs (see
      // constants/adviser-channel-companies.js). Null shows every company
      // the backend returns — the /adviser page behaviour.
      allowedCompanyIds: {
        type: Array,
        default: null,
      },
      // Channel-specific FAQ / Terms copy (see constants/adviser-enrolment/).
      // Null falls back to the built-in /adviser (PFA/DFA/AFD) copy, including
      // the legacy switchover overrides below — those match on /adviser
      // question text, so they are bypassed entirely when an override is set.
      faqCategoriesOverride: {
        type: Array,
        default: null,
      },
      termsOverride: {
        type: Array,
        default: null,
      },
      faqsBannerTitle: {
        type: String,
        default: 'Adviser FAQs',
      },
      customerBrochureBannerTitle: {
        type: String,
        default: 'Customer Brochure',
      },
      // Channel pages can hide download banners whose PDFs don't have a
      // channel-specific version yet.
      showFaqsBanner: {
        type: Boolean,
        default: true,
      },
      showWhatsNewBanner: {
        type: Boolean,
        default: true,
      },
      showCustomerBrochureBanner: {
        type: Boolean,
        default: true,
      },
      // Label for the company/channel dropdown. The vee-validate field name
      // stays "channel" so the error message reads naturally.
      companyFieldLabel: {
        type: String,
        default: 'Select your Old Mutual Channel',
      },
      // Channel pages built on AdviserChannelPage render the FAQ accordion
      // on /adviser/faqs instead, so they turn the inline FAQ list off.
      showFaqs: {
        type: Boolean,
        default: true,
      },
      // "web" applies the OMW-419 form card styling (outlined 16px fields,
      // card shadow, 816px column). "legacy" leaves /mfc exactly as it was.
      cardStyle: {
        type: String,
        default: 'legacy',
        validator: value => ['legacy', 'web'].includes(value),
      },
    },
    data() {
      return {
        formValid: false,
        formData: {
          firstNames: '',
          lastName: '',
          email: '',
          adviserCode: '',
          companyId: null,
          password: '',
          confirmPassword: '',
          acceptedTerms: false,
        },
        showPassword: false,
        showConfirmPassword: false,
        // Populated by the adviserEnrolmentCompanies Apollo query — IDs come
        // from the DB so trading names always reflect the current admin state.
        // The backend returns the records in display order, so no client-side
        // sort is needed.
        adviserEnrolmentCompanies: [],
        faqCategories: [
          {
            title: 'Legality & Security',
            faqs: [
              {
                question: 'Is creating a will online with Old Mutual valid?',
                answer:
                  '<strong>A</strong>: Yes. A will created through Old Mutual is binding as long as it meets the necessary legal requirements, such as proper signing and witnessing. Clients will receive clear instructions to ensure compliance.',
              },
              {
                question:
                  "How does Old Mutual ensure the security of my clients' personal information?",
                answer:
                  '<strong>A</strong>: Old Mutual uses industry-leading encryption and security protocols to protect sensitive data. All personal and financial details remain confidential and are never shared without consent.',
              },
              {
                question:
                  "Who is eligible to use Old Mutual's online will service?",
                answer:
                  '<strong>A</strong>: The service is available to South African residents aged 16 years or older.',
              },
            ],
          },
          {
            title: 'Making Changes & Storage',
            faqs: [
              {
                question:
                  "Can my client make changes to their will after they've created it?",
                answer:
                  "<strong>A</strong>: Yes! Clients can edit their will at any time before finalizing and signing it. It's advisable to update a will after major life events such as marriage, divorce, having children, or acquiring significant assets.",
              },
              {
                question: 'Does Old Mutual charge a storage fee?',
                answer:
                  '<strong>A</strong>: No. Storage and one collection of the signed will are free of charge once per year. This ensures the original will is securely stored and accessible when needed.',
              },
            ],
          },
          {
            title: 'Process & Costs',
            faqs: [
              {
                question:
                  'What does it cost for my client to create a will with Old Mutual?',
                answer:
                  '<strong>A</strong>: Absolutely nothing. The will drafting service is completely free.',
              },
              {
                question: 'How does the process work?',
                answer:
                  '<strong>A</strong>:<br>1. The adviser guides the client through the online will creation process.<br>2. The client reviews, prints, signs, and has their will witnessed as per legal requirements.<br>3. The signed will is uploaded onto the system for validation.<br>4. The signed & validated will is collected (free once per year) and securely stored by Old Mutual.',
              },
              {
                question:
                  'What happens if a client does not sign or return their will?',
                answer:
                  '<strong>A</strong>: If a will is not signed and validated, it will not be legally binding. Advisers should encourage clients to follow through with signing and submission to complete the process. It is to the benefit of the customer to store his / her will with Old Mutual, but this is not a requirement. This is a benefit, free of charge, to the customer.',
              },
            ],
          },
          {
            title: 'Adviser Incentive & Earnings',
            faqs: [
              {
                question:
                  'How do I qualify for the R750 (pre-tax) incentive per will?',
                answer:
                  '<strong>A</strong>:<br>The will must be created, signed and validated before or on 31 December 2025.<br>Only wills facilitated through registered Old Mutual advisers qualify.<br>Payments will be processed after verification of each successful will submission.',
              },
              {
                question:
                  'How do I register as an adviser on the Old Mutual Will platform?',
                answer:
                  '<strong>A</strong>:<br>1. Click <a href="#get-registered">here</a> to register.<br>2. Complete the sign-up form and receive login details.<br>3. Start assisting clients and earning incentives.',
              },
            ],
          },
          {
            title: 'Additional Adviser Support',
            faqs: [
              {
                question: 'Can I track my submitted wills and earnings?',
                answer:
                  '<strong>A</strong>: Yes, advisers will have access to a dashboard where they can track submitted wills and qualifying incentives.',
              },
              {
                question:
                  'Where can I get assistance if I or my clients need help?',
                answer:
                  '<strong>A</strong>: Our support team is ready to assist! Contact us at:<br>• adviser@oldmutualwill.co.za<br>• 021 753 3248<br>(Monday – Friday, 08:00 – 17:00)',
              },
            ],
          },
        ],
        terms: [
          `The pilot will run from 1 September 2025 until 31 December 2025.`,
          `To qualify for the incentive, a client's will must be created, signed and validated between 1 September and 31 December 2025.`,
          `There is no limit per adviser.`,
          `Weekly reports will be provided to PF to share with the relevant advisers`,
          `PF will make payment to the relevant advisers via OMIpay.`,
          `All queries must be directed to PF that will resolve the issues with Old Mutual Will.`,
          `Please note, the current incentive applies to PFA, DFA, and AFD adviser channels. Participation and incentive models for other distribution segments are underway. When confirmed, the details will be officially communicated to each channel.`,
        ],
      };
    },
    apollo: {
      adviserEnrolmentCompanies: {
        query: ADVISER_ENROLMENT_COMPANIES_QUERY,
        fetchPolicy: 'cache-and-network',
      },
    },
    methods: {
      isAfterSwitchover() {
        if (!this.switchoverDate) return true;
        const today = new Date();
        const switchover = new Date(this.switchoverDate);
        const isStaging = window.location.href.includes('staging');
        return isStaging || today >= switchover;
      },
      getTerm(term, index) {
        if (this.termsOverride) return term;
        if (!this.isAfterSwitchover()) {
          return term;
        }

        const updatedTerms = {
          0: `The incentive will run from 1 January 2026 until 31 December 2026.`,
          1: `To qualify for the incentive, a client's will must be validated between 1 January 2026 until 31 December 2026.`,
          2: `Each adviser can earn a maximum of R60 000 per annum from this incentive.`,
          3: `Payments are triggered based on validation for a client's first will on the Old Mutual Will platform.`,
          4: `PF will make payment to the relevant advisers via OMIpay.`,
          5: `All queries can be directed to Old Mutual Will via email (adviser@oldmutualwill.co.za)`,
          6: `Please note, the current incentive applies to PFA, DFA, and AFD adviser channels. 
             Participation and incentive models for other distribution segments are underway. 
             When confirmed, the details will be officially communicated to each channel.`,
        };

        return updatedTerms[index] !== undefined ? updatedTerms[index] : term;
      },
      // FAQ answers render via v-html, so their in-page anchor links (e.g.
      // "Click here to register" → #get-registered) can't use Vue handlers.
      onFaqAnswerClick: smoothScrollToAnchor,
      getFaqQuestion(faq) {
        if (this.faqCategoriesOverride) return faq.question;
        const isAfterSwitchover = this.isAfterSwitchover();

        if (
          faq.question ===
          'How do I qualify for the R750 (pre-tax) incentive per will?'
        ) {
          return isAfterSwitchover
            ? 'How do I qualify for the R500 (pre-tax) incentive per will?'
            : faq.question;
        }

        return faq.question;
      },
      getFaqAnswer(faq) {
        if (this.faqCategoriesOverride) return faq.answer;
        const isAfterSwitchover = this.isAfterSwitchover();

        if (
          faq.question ===
          'What happens if a client does not sign or return their will?'
        ) {
          return isAfterSwitchover
            ? '<strong>A</strong>: If a will is not signed, it will not be valid. Advisers should encourage clients to follow through with signing and submission to complete the process. It is to the benefit of the customer to store his / her will with Old Mutual, but this is not a requirement. This is a benefit, free of charge, to the customer.'
            : faq.answer;
        }

        if (
          faq.question ===
          'How do I qualify for the R750 (pre-tax) incentive per will?'
        ) {
          return isAfterSwitchover
            ? `<strong>A</strong>:<br>The will must be validated between 1 January 2026 and 31 December 2026.<br>Only wills facilitated through registered Old Mutual advisers qualify. 
            Payments are triggered for a client's first will on the Old Mutual Will platform. 
            Payments will be processed after verification of each successful will submission.
                  <br/></br/>

                  Please note, the current incentive applies to PFA, DFA, and AFD adviser channels.
                  Participation and incentive models for other distribution segments are underway.
                  When confirmed, the details will be officially communicated to each channel.
            `
            : faq.answer;
        }

        return faq.answer;
      },
      downloadFAQs(event) {
        if (event) event.preventDefault();
        try {
          const pdfName = this.isAfterSwitchover()
            ? '2026-Adviser-Enrolment-FAQs.pdf'
            : 'Adviser-Enrolment-FAQs.pdf';
          const link = document.createElement('a');
          link.href = `pdfs/${pdfName}`;
          link.download = pdfName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (error) {
          console.error('Error opening PDF:', error);
        }
      },
      downloadWhatsNew(event) {
        if (event) event.preventDefault();
        try {
          const pdfName = 'OMP-Adviser-OMW-OMP_v2.pdf';
          const link = document.createElement('a');
          link.href = `pdfs/${pdfName}`;
          link.download = pdfName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (error) {
          console.error('Error opening PDF:', error);
        }
      },
      downloadCustomerBrochure(event) {
        if (event) event.preventDefault();
        try {
          const link = document.createElement('a');
          link.href = 'pdfs/Customer-Brochure.pdf';
          link.download = 'Customer-Brochure.pdf';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (error) {
          console.error('Error opening PDF:', error);
        }
      },
      normaliseAdviserCode() {
        this.formData.adviserCode = (this.formData.adviserCode || '').replace(
          /\s+/g,
          ''
        );
      },
      async submitForm() {
        this.normaliseAdviserCode();
        await this.$nextTick();
        const isValid = await this.$refs.observer.validate();
        if (isValid) {
          this.register();
        }
      },
      async register() {
        try {
          const result = await this.$apollo.mutate({
            mutation: REGISTER_ADVISER_MUTATION,
            variables: {
              firstNames: this.formData.firstNames,
              lastName: this.formData.lastName,
              email: this.formData.email,
              adviserCode: this.formData.adviserCode,
              companyId: this.formData.companyId,
              password: this.formData.password,
            },
          });

          const registerAdviser = result.data.registerAdviser;
          this.$store.dispatch('login', {
            accessToken: registerAdviser.accessToken,
            refreshToken: registerAdviser.refreshToken,
            testatorId: registerAdviser.testatorId,
            userId: registerAdviser.userId,
            willCountry: registerAdviser.willCountry,
            companyId: registerAdviser.companyId,
          });
          this.setSentryUser(registerAdviser.userId);
          this.$router.push('/advisors');
        } catch (error) {
          if (error.message.includes('users.index_users_on_email')) {
            return;
          }
          const cleanError = error.message.replace('GraphQL error: ', '');
          this.$emit('showErrorMessage', cleanError);
        }
      },
      setSentryUser(userId) {
        Sentry.setUser({ id: userId });
      },
    },
    computed: {
      isMobile() {
        return this.$isMobileDevice();
      },
      legalitySecurityTitle() {
        return this.isAfterSwitchover()
          ? 'Validity & Security'
          : 'Legality & Security';
      },
      displayedFaqCategories() {
        return this.faqCategoriesOverride || this.faqCategories;
      },
      displayedTerms() {
        return this.termsOverride || this.terms;
      },
      hasSelectedCompany() {
        return !!this.formData.companyId;
      },
      isMfcCompany() {
        return [
          ...MFC_COMPANY_IDS,
          ...NON_PRODUCTION_MFC_TEST_COMPANY_IDS,
        ].includes(Number(this.formData.companyId));
      },
      adviserCodeRules() {
        return this.isMfcCompany ? 'required' : 'required|adviserCode';
      },
      sortedCompanies() {
        // Backend returns the records already in display order. We just map
        // them into the {text, value} shape v-select expects. Trim trading
        // names defensively — some DB rows have legacy trailing whitespace.
        return (this.adviserEnrolmentCompanies || [])
          .filter(
            c =>
              !this.allowedCompanyIds ||
              this.allowedCompanyIds.includes(Number(c.id))
          )
          .map(c => ({
            text: (c.tradingName || '').trim(),
            value: c.id,
          }));
      },
    },
  };
</script>

<style scoped lang="scss">
  .adviser-enrolment-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    overflow: visible;
  }

  // Keeps the heading clear of the fixed navbar when the FAQ's
  // "Click here to register" anchor link scrolls to it.
  #get-registered {
    scroll-margin-top: 110px;
  }

  .adviser-enrolment-content {
    width: 100%;
    max-width: 768px;
    justify-self: center;
    position: relative;
    z-index: 1;

    .adviser-enrolment-form {
      padding: 5%;
      background-color: white;
    }

    .download-faqs-banner-container {
      padding-top: 80px;
      padding-bottom: 80px;

      &.customer-brochure {
        padding-top: 0;
        padding-bottom: 80px;
      }

      .download-faqs-banner {
        .content {
          background-color: #ffffff;
          padding: 47px;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-start;

          .download-faqs-banner-button {
            margin-left: auto;
          }
          @media only screen and (max-width: 959px) {
            .download-faqs-banner-button {
              margin-left: 0;
            }
          }

          p {
            font-family: Montserrat;
            margin: 0;
            font-size: 28px;
            font-weight: 300;
            line-height: 34px;
            letter-spacing: -0.0025em;
            text-align: left;
            margin-right: 30px;

            span {
              font-family: Montserrat;
              font-size: 28px;
              font-weight: 500;
              line-height: 34px;
              letter-spacing: -0.0025em;
            }
          }
          @media only screen and (max-width: 959px) {
            .content {
              justify-content: center !important;
            }
            p {
              text-align: center !important;
            }
          }
        }
      }
    }
  }

  section {
    margin-bottom: 60px;
  }

  .semi-bold {
    font-weight: 600 !important;
  }
  .article-link {
    color: #282828;
    font-weight: 500;
  }

  h1 {
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-style: normal;
    font-size: 28px;
    line-height: 34px;
    margin-bottom: 24px;
    &.intro {
      font-size: 22px;
      font-weight: 400;
      line-height: 26px;
    }
  }
  h2 {
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-style: normal;
    font-size: 22px;
    line-height: 26px;
    margin: 40px 0 24px 0;
    &.sub-heading-one {
      font-size: 18px;
      font-weight: 600;
      line-height: 24px;
    }
    &.sub-heading-two {
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
    }
    &.om-green {
      color: #50b848;
    }
  }

  .article-cta {
    color: #50b848;
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    a {
      color: #50b848;
      font-family: 'Montserrat', sans-serif;
      font-size: 16px;
      font-weight: 600;
      line-height: 22px;
    }
  }
  p,
  ul,
  li {
    font-family: 'Montserrat', sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: 16px;
    line-height: 22px;
  }
  blockquote {
    color: #50b848;
    border-left: 4px solid #50b848;
    padding-left: 24px;
    font-size: 22px;
    font-weight: 400;
    line-height: 26px;
    text-align: left;
    margin: 40px 0;
    ul {
      li {
        list-style-type: disc;
        font-family: 'Montserrat', sans-serif;
        font-size: 16px;
        font-weight: 400;
        line-height: 28px;
        text-align: left;

        &::marker {
          color: #50b848;
        }
      }
      p {
        color: #282828;
        font-family: 'Montserrat', sans-serif;
        font-weight: 400;
        font-style: normal;
        font-size: 16px;
        line-height: 22px;
      }
      &.no-paragraphs {
        li {
          color: #282828;
        }
      }
    }
  }
  ul {
    margin: 24px 0;
    p {
      margin: 10px 0;
    }
  }

  .faq-category {
    margin-bottom: 40px;

    .faq-item {
      margin-bottom: 24px;
    }
  }

  .terms-link {
    color: #009677;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
  @media only screen and (min-width: 1264px) and (max-width: 1294px) {
    .download-faqs-banner-container {
      .download-faqs-banner {
        padding-left: 140px;
        padding-right: 140px;
      }
    }
  }

  @media only screen and (min-width: 960px) and (max-width: 1263px) {
    .download-faqs-banner-container {
      .download-faqs-banner {
        padding-left: 80px;
        padding-right: 80px;
      }
    }
  }

  @media only screen and (min-width: 0px) and (max-width: 959px) {
    .download-faqs-banner-container {
      padding-top: 50px;
      padding-bottom: 50px;
      .download-faqs-banner {
        padding-left: 26px;
        padding-right: 26px;
        .content {
          flex-direction: column;
          padding: 26px !important;
          p {
            margin-right: 0 !important;
            margin-bottom: 30px !important;
          }
        }
      }
    }
  }
  // OMW-419 form card (spec 4.6). Scoped under .web-style so /mfc is untouched.
  .web-style {
    .adviser-enrolment-content {
      max-width: 816px;

      .adviser-enrolment-form {
        padding: 60px 60px 40px;
        box-shadow: 4px 16px 20px rgba(0, 0, 0, 0.06);
      }

      h1 {
        letter-spacing: -0.07px;
      }
      p {
        line-height: 24px;
      }

      ::v-deep .v-text-field--outlined fieldset {
        border-radius: 16px;
        border: 1px solid #cccccc;
      }
      ::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
        border: 2px solid #306e2b;
      }
      ::v-deep .v-text-field--outlined.error--text fieldset {
        border: 2px solid #910822;
      }
      ::v-deep .v-text-field--outlined .v-label {
        font-size: 16px;
      }
      ::v-deep .v-text-field--outlined .v-label--active {
        font-size: 12px;
        background: #ffffff;
        padding: 0 4px;
      }
      ::v-deep .v-text-field--outlined.v-input--is-focused .v-label {
        color: #306e2b;
      }
      ::v-deep .v-messages__message {
        font-size: 12px;
        line-height: 16px;
      }
    }

    @media only screen and (max-width: 959px) {
      .adviser-enrolment-content .adviser-enrolment-form {
        padding: 32px 20px 24px;
      }
    }
  }
</style>
